import { format } from 'date-fns';

import { AnimatePresence } from 'motion/react';
import { motion } from 'motion/react';
import { DateRange, SelectRangeEventHandler } from 'react-day-picker';

import { cn } from '../lib';
import { Button, ButtonProps } from './button';
import { Calendar } from './calendar';
import { DashboardDialogProps } from './dashboard-dialog';
import { Typography } from './typography';

export const DATE_RANGE_PICKER_SUBTITLE: Record<keyof DateRange, string> = {
  from: 'Select the start day',
  to: 'Select the end day',
};

export interface SelectedRangeDate {
  start: Date;
  end: Date;
  label?: string;
}

interface DateRangePickerProps {
  range: DateRange | undefined;
  setRange: (range: DateRange | undefined) => void;
  dateRangePickerSubtitle: string;
  setDateRangePickerSubtitle: (subtitle: string) => void;
  setSelectedDate: (date: SelectedRangeDate | null) => void;
  onClose: VoidFunction;
}

export const TimeRange = ({
  className,
  ...props
}: Omit<ButtonProps, 'children'>) => {
  return (
    <Button
      variant="dark-secondary"
      size="md"
      className={cn(
        'flex h-[84px] w-full flex-col gap-1 rounded-3xl text-base leading-5',
        className,
      )}
      {...props}
    >
      Time range
      <Typography variant="14-r" color="text-base-100">
        From any day
      </Typography>
    </Button>
  );
};

const MotionButton = motion.create(Button);

export const DATE_RANGE_PICKER_CONFIG: Partial<DashboardDialogProps> = {
  title: 'Select a range',
  animationProps: {
    id: 'range',
  },
  hideCloseButton: true,
  height: 'fixed',
  size: 'lg',
};

export const DateRangePicker = ({
  range,
  setRange,
  dateRangePickerSubtitle,
  setDateRangePickerSubtitle,
  setSelectedDate,
  onClose,
}: DateRangePickerProps) => {
  const onSelect: SelectRangeEventHandler = (range) => {
    setRange(range);
    if (range?.from && range.to) {
      setDateRangePickerSubtitle(
        `${format(range.from, 'MMM dd, yyyy')} - ${format(range.to, 'MMM dd, yyyy')}`,
      );
    } else {
      setDateRangePickerSubtitle(
        range?.from
          ? DATE_RANGE_PICKER_SUBTITLE.to
          : DATE_RANGE_PICKER_SUBTITLE.from,
      );
    }
  };

  const onFilter = () => {
    if (!range?.from || !range.to) return;

    setSelectedDate({
      start: range.from,
      end: range.to,
      label: dateRangePickerSubtitle,
    });

    onClose();
  };

  return (
    <div className="flex flex-col items-center gap-y-4">
      <Calendar
        numberOfMonths={2}
        onSelect={onSelect}
        selected={range}
        mode="range"
        captionLayout="dropdown-buttons"
      />

      <AnimatePresence>
        {range?.from && range.to && (
          <MotionButton
            size="lg"
            variant="contrast-500"
            className="w-full max-w-[360px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onFilter}
          >
            Filter
          </MotionButton>
        )}
      </AnimatePresence>
    </div>
  );
};
